// View Team Permissions Modal Component

import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Search, Check, X } from 'lucide-react';
import type { Team, Permission, PermissionDefinition } from '@/types/teams';

// Helper function to check if a sub-permission matches the search query - extracted to reduce nesting depth (S2004)
function subPermissionMatchesQuery(sp: PermissionDefinition, query: string): boolean {
  return (
    sp.displayName?.toLowerCase().includes(query) ||
    sp.description?.toLowerCase().includes(query)
  ) ?? false;
}

interface ViewTeamPermissionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teams: Team[];
  permissions: Permission[];
}

export function ViewTeamPermissionsModal({
  open,
  onOpenChange,
  teams,
  permissions,
}: Readonly<ViewTeamPermissionsModalProps>) {
  const [searchQuery, setSearchQuery] = useState('');

  // Get the selected team (only one team should be selected for this modal)
  const selectedTeam = teams[0];

  // Group permissions by category
  const groupedPermissions = useMemo(() => {
    const grouped: Record<string, Permission[]> = {};
    permissions.forEach(perm => {
      const category = perm.category ?? 'Other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(perm);
    });
    return grouped;
  }, [permissions]);

  // Filter permissions by search
  const filteredCategories = useMemo(() => {
    if (!searchQuery) return groupedPermissions;

    const query = searchQuery.toLowerCase();
    const filtered: Record<string, Permission[]> = {};

    Object.entries(groupedPermissions).forEach(([category, perms]) => {
      const matchingPerms = perms.filter(
        perm =>
          perm.displayName?.toLowerCase().includes(query) ||
          perm.description?.toLowerCase().includes(query) ||
          perm.subPermissions?.some(sp => subPermissionMatchesQuery(sp, query))
      );

      if (matchingPerms.length > 0) {
        filtered[category] = matchingPerms;
      }
    });

    return filtered;
  }, [groupedPermissions, searchQuery]);

  // Check if a sub-permission is granted for the selected team
  const isSubPermissionGranted = (permissionId: string, subPermKey: string): boolean => {
    if (!selectedTeam) return false;
    const teamPerm = selectedTeam.permissions.find(p => p.permissionId === permissionId);
    if (!teamPerm?.granted) return false;
    const subPerm = teamPerm.subPermissions?.find(sp => sp.key === subPermKey);
    return subPerm?.granted ?? false;
  };

  const getStatusBadge = (isGranted: boolean) => {
    if (isGranted) {
      return (
        <Badge variant="default" className="gap-1">
          <Check className="h-3 w-3" />
          Enabled
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="gap-1">
        <X className="h-3 w-3" />
        Disabled
      </Badge>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            Permissions for {selectedTeam?.name || 'Team'}
          </DialogTitle>
          <DialogDescription>
            View all permissions for this team
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search permissions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Permissions List */}
        <ScrollArea className="flex-1 min-h-0 -mx-6 px-6">
          <div className="space-y-6">
            {Object.keys(filteredCategories).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p className="text-sm">No permissions found</p>
              </div>
            ) : (
              Object.entries(filteredCategories).map(([category, perms]) => (
                <div key={category}>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-3">
                    {category}
                  </h3>
                  <div className="space-y-3">
                    {perms.map((permission) => (
                      <div
                        key={permission.id}
                        className="p-4 rounded-lg border bg-card"
                      >
                        <div className="mb-3">
                          <h4 className="font-medium">{permission.displayName}</h4>
                          {permission.description && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {permission.description}
                            </p>
                          )}
                        </div>

                        {/* Sub-permissions */}
                        {permission.subPermissions && permission.subPermissions.length > 0 && (
                          <div className="space-y-2 pt-3 border-t">
                            {permission.subPermissions.map((subPerm) => {
                              const isGranted = isSubPermissionGranted(permission.id, subPerm.key);

                              return (
                                <div
                                  key={subPerm.id}
                                  className="flex items-start justify-between gap-3 p-2 rounded bg-muted/50"
                                >
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium">{subPerm.displayName}</p>
                                    {subPerm.description && (
                                      <p className="text-xs text-muted-foreground mt-0.5">
                                        {subPerm.description}
                                      </p>
                                    )}
                                  </div>
                                  {getStatusBadge(isGranted)}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
